import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, generatePath } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ButtonPseudo,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  ButtonPrimary,
  ResponseError,
  responseErrorsResolver,
  Table,
  formatBalance,
  formatDate,
  Currency,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetCustomerBeneficiaryContractQuery,
  useUpdateContractMutation,
  useUpdateContractStatusMutation,
  useLazyGetContractDocumentFileQuery,
} from '../store/services/customers';
import {
  BeneficiaryContractFormValues,
  BeneficiaryStatus,
  ContractStatement,
  CustomerStatus,
  CustomerType,
} from '../types';
import { columns, columnsLabels } from './variables/transactions';
import { clearContractState } from '../store/slices/beneficiaries/beneficiaries';
import BeneficiaryContractDetails from '../../../components/beneficiaries/contract-details';
import ROUTES from '../../../routes';

import cn from './beneficiaries.module.css';

const formatContractStatementsList = (
  list: ContractStatement[],
): ContractStatement[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      createdAt: <div>{formatDate(item.createdAt, true)}</div>,
      currencyId: <div>{item?.currency?.name}</div>,
      sum: <div>{formatBalance(item.sum, item.currency as Currency)}</div>,
      creditSum: (
        <div>{item?.generalLedgerTransaction?.creditAccountNumber || '-'}</div>
      ),
      debitSum: (
        <div>{item?.generalLedgerTransaction?.debitAccountNumber || '-'}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const BeneficiaryContract = ({ type }: { type: CustomerType }) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [panelState, setPanelState] = useState<'details' | 'transactions'>(
    'details',
  );
  const [uploadedDocuments, setUploadedDocuments] = useState<
    { uploadId: string; createdAt: string; name?: string; id: string }[]
  >([]);
  const [updateError, setUpdateError] = useState('');
  const [downloadId, setDownloadId] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { customerId, beneficiaryId, contractId } = useParams();

  const {
    customerBeneficiaries: { beneficiaryContract, beneficiary },
    customersDictionaries: {
      baseCurrency,
      currencies,
      currenciesList,
      activePermissions,
    },
    individualCustomers: {
      info: {
        general: { baseInformation: individualBaseInformation },
      },
    },
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
  } = useAppSelector((state) => state);

  const organizationStatus =
    type === CustomerType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const [updateContract, { isLoading: updateInProgress }] =
    useUpdateContractMutation();
  const [updateContractStatus, { isLoading: updateStatusInProgress }] =
    useUpdateContractStatusMutation();
  const [
    getContractFile,
    { isLoading: downloadInProgress, isFetching: downloadIsFetching },
  ] = useLazyGetContractDocumentFileQuery();

  const {
    control,
    reset,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm<BeneficiaryContractFormValues>();

  const [getContract, { isLoading, isFetching }] =
    useLazyGetCustomerBeneficiaryContractQuery();

  const onRowClick = (id: string) => {
    const currentStatement = beneficiaryContract?.statementItems?.edges?.find(
      (item) => item.node.id === id,
    );

    if (currentStatement) {
      setPanelIsOpen(false);

      let link = '';

      if (
        currentStatement.node.type === 'INTERNAL' &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST)
      ) {
        link = `/transactions/internal/${currentStatement?.node.transactionId}`;
      } else if (
        currentStatement.node.type === 'OUTGOING' &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_OUTGOING_LIST)
      ) {
        link = `/transactions/outgoing/${currentStatement?.node.transactionId}`;
      }

      window.open(link, '_blank');
    }
  };

  const formattedData = useMemo(() => {
    const data = formatContractStatementsList(
      beneficiaryContract?.statementItems?.edges?.map(
        (item) => item.node,
      ) as ContractStatement[],
    );

    return data;
  }, [beneficiaryContract]);

  const getFile = async (id: string) => {
    setDownloadId(id);
    setDownloadError('');

    const response = (await getContractFile({
      id: beneficiaryId as string,
      contractId: contractId as string,
      documentId: id,
    })) as {
      error?: ResponseError;
      data?: any;
    };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setDownloadError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setDownloadError(response.error.data.message);
        } else {
          setDownloadError('Internal Server Error. Try Again');
        }
      }
    } else if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      const currentDocument = uploadedDocuments?.find((item) => item.id === id);

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = currentDocument?.name || 'Document';
      hiddenElement.click();
      setDownloadId('');
    }
  };

  const updateContractAction = handleSubmit(async (values) => {
    setUpdateError('');

    const body: {
      id: string;
      name: string;
      description?: string;
      sum?: number;
      currencyId?: string;
    } = {
      id: values.id,
      name: values.name,
      description: values.description,
      currencyId: values?.currencyId || '',
    };

    if (values?.sum) {
      body.sum = Number(values?.sum);
    }

    if (values?.currencyId) {
      body.currencyId = values.currencyId;
    }

    if (values.description) {
      body.description = values?.description;
    }

    const response = (await updateContract({
      id: beneficiaryId as string,
      contractId: contractId as string,
      body,
    })) as { error?: ResponseError };

    if (
      values.status !== beneficiaryContract?.status &&
      beneficiaryContract?.status !== 'REJECTED'
    ) {
      await updateContractStatus({
        id: beneficiaryId as string,
        contractId: contractId as string,
        body: {
          status: values.status as BeneficiaryStatus,
        },
      });
    }

    if (response.error && response.error.data.message) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setUpdateError,
      });
    }
  });

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      dispatch(clearContractState());

      navigate(
        generatePath(
          type === CustomerType.INDIVIDUAL
            ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARY
            : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARY,
          {
            customerId: customerId as string,
            beneficiaryId: beneficiaryId as string,
          },
        ),
      );
    }, 100);
  };

  useEffect(() => {
    if (contractId) {
      getContract({ beneficiaryId: beneficiaryId as string, contractId });
    }
  }, [contractId]);

  useEffect(() => {
    if (beneficiaryContract && currenciesList) {
      reset({
        id: beneficiaryContract.id,
        name: beneficiaryContract.name,
        description: beneficiaryContract.description,
        sum: beneficiaryContract?.sum,
        status: beneficiaryContract?.status,
        currencyId: beneficiaryContract?.currencyId,
      });

      if (beneficiaryContract?.currencyId) {
        setSelectedCurrency(
          currenciesList?.find(
            (item) => item.id === beneficiaryContract?.currencyId,
          ),
        );
      }

      setUploadedDocuments(
        beneficiaryContract?.supportingDocuments?.edges?.map(
          (item) => item.node,
        ) || [],
      );
    }
  }, [beneficiaryContract, currenciesList]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <div className={cn.contractTabsWrapper}>
          <button
            type="button"
            className={`${cn.contractTabButton} ${
              panelState === 'details' ? cn.active : ''
            }`}
            onClick={() => setPanelState('details')}
          >
            Details
          </button>
          <button
            type="button"
            className={`${cn.contractTabButton} ${
              panelState === 'transactions' ? cn.active : ''
            }`}
            onClick={() => setPanelState('transactions')}
          >
            Transactions
          </button>
        </div>
        {panelState === 'transactions' && (
          <Table
            columns={columns}
            tableFormClassName={cn.transactionTable}
            data={formattedData || []}
            onSort={() => {}}
            filters={{}}
            sort={{}}
            columnsLabels={columnsLabels}
            onFilterChange={() => {}}
            onRowClick={(id) => onRowClick(id)}
          />
        )}
        {panelState === 'details' && (
          <BeneficiaryContractDetails
            control={control}
            beneficiaryContract={beneficiaryContract}
            errors={errors}
            baseCurrency={baseCurrency}
            beneficiaryName={beneficiary?.name}
            documents={uploadedDocuments}
            getFile={getFile}
            downloadId={downloadId}
            downloadError={downloadError}
            downloadInProgress={downloadInProgress || downloadIsFetching}
            currencies={currencies}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            currenciesList={currenciesList}
            organizationStatus={organizationStatus}
          />
        )}
      </>
    );
  }

  return (
    <SidePanel
      isOpen={panelIsOpen}
      onClose={handleClose}
      type={SidePanelType.Customers}
      title="Contract details"
      errorSidepanel={updateError}
      sidepanelExpandedKey="beneficairy-contract-details"
      footer={
        <>
          {beneficiaryContract?.status !== 'REJECTED' &&
            organizationStatus !== CustomerStatus.CLOSED && (
              <ButtonPrimary
                text={
                  updateInProgress || updateStatusInProgress
                    ? 'Loading...'
                    : 'Save'
                }
                disabled={
                  updateInProgress ||
                  updateStatusInProgress ||
                  beneficiaryContract?.status === 'REJECTED'
                }
                onClick={updateContractAction}
                bold
              />
            )}

          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </>
      }
      closeOnClickOutside
    >
      {content}
    </SidePanel>
  );
};

export default BeneficiaryContract;
